/**
 * External Imports
 */
import {
  FC,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { format, sub } from 'date-fns';

/**
 * Nexus Imports
 */
import { NeuIcon, NeuTab } from '@neutron/react';

/**
 * Internal Imports
 */
import EncountersTable from '../../components/customTables/admin/encountersTable';
import EncountersHistoryTable from '../../components/customTables/admin/encountersHistoryTable';
import { Dropdown } from '../../components/shared/dropdowns';

import { RootState } from '../../redux/store';
import {
  clearPatientRoundHistory,
  clearTasksByAccountNum
} from '../../redux/actions';
// import { getRound } from '../../redux/actions/Round.action';
import { useLazyGetEncountersQuery } from '../../redux/apis/Admin.api';

import { useDebounceValue } from '../../utils/debouncers';
import { date, toTitleCaseSpecial } from '../../utils/helpers';

/**
 * Style Imports
 */
import {
  EncountersContainer,
  EncountersSidePanelContainer,
  EncountersSidePanelSubheader,
  EncounterSidePanelInput,
  EncounterSidePanelInputLabels,
  BackText,
  EncounterHistorySidePanelLabels,
  EncounterHistorySidePanelData,
  EncountersHistoryTabs,
  JsonViewContainer,
  JsonView,
  EncountersHistoryTableContainer
} from './styles/AdminViews.styles';

/**
 * Global Type Definition Imports
 */
import {
  Encounter,
  Facility,
  Patient,
  RoundHistory
} from '../../config/interfaces';

interface EncountersProps {
  facilities: Facility[];
  patientRoundHistory: RoundHistory[];
  selectedFacility: string;
}

interface UserInputsT {
  startday: string;
  endday: string;
  facilityId?: string;
}

const Encounters: FC<EncountersProps> = ({
  facilities,
  patientRoundHistory,
  selectedFacility
}) => {
  const dispatch = useDispatch();

  // selected facility
  const [facility, setFacility] = useState<Facility | null | undefined>(null);
  // value to display in the facility input field
  const [facilityFieldValue, setFacilityFieldValue] = useState<string | null>(
    null
  );

  const [userInputs, setUserInputs] = useState<UserInputsT>({
    startday: format(new Date(), 'yyyy-MM-dd'),
    endday: format(new Date(), 'yyyy-MM-dd')
  });
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const [encounterHistory, setEncounterHistory] = useState<Encounter | null>(
    null
  );
  const [roundHistory, setRoundHistory] = useState<RoundHistory[] | null>(null);
  const [historyViewType, setHistoryViewType] = useState<string>('table');
  const [patientToOpen, setPatientToOpen] = useState<
    RoundHistory | Patient | null
  >(null);

  const [isFacilityOpen, setIsFacilityOpen] = useState(false);
  const [activeSearch, setActiveSearch] = useState<string>('');
  const [query, setQuery] = useState('');

  const firstUpdate: MutableRefObject<boolean> = useRef(true); // to detect first render

  const debouncedQuery = useDebounceValue(query, 250);

  const navigate = useNavigate();

  const [
    getEncountersQuery,
    {
      data: encountersData
      // isError: isGetEncountersError,
      // isFetching: isGetEncountersFetching,
      // isLoading: isGetEncountersLoading
    }
  ] = useLazyGetEncountersQuery();

  // TODO: Are RoundHistory and RoundsReport the same type
  const gotoRoundPage = (round: RoundHistory) => {
    setPatientToOpen(round);
    // TODO: Add later with round refactor Phase II
    // if (round.roundId) {
    //   dispatch(
    //     getRound({
    //       roundId: round.roundId,
    //       fromHistoryDrawer: false,
    //       type: 'patient',
    //       accountNum: round.accountNum,
    //       unitId: round.unitId,
    //     })
    //   );
    // }
  };

  const facilitySearchResult = useMemo(() => {
    switch (activeSearch) {
      case 'facility':
        return facilities.filter((f: Facility) =>
          f.facility.toLowerCase().includes(debouncedQuery.trim().toLowerCase())
        );
      default:
        return undefined;
    }
  }, [activeSearch, debouncedQuery, facilities]);

  const handleFacilitySelected = (f: Facility) => {
    setFacility(f);
    setFacilityFieldValue(f.facility);
    setActiveSearch('');
    setQuery('');
  };

  const handleInputs = (e: CustomEvent<any>) => {
    const { name, value } = e.target as HTMLInputElement;

    setUserInputs({ ...userInputs, [name]: value });
  };

  const handleHideHistory = () => {
    setShowHistory(false);
    setEncounterHistory(null);
    setHistoryViewType('table');
    setRoundHistory([]);
  };

  const handleTypeAheadChange = useCallback(
    (e: any) => setQuery(e.target.value),
    []
  );

  const getAccessPlacesById = (id?: string) => {
    const obj = facilities.find((item: Facility) => item.facilityId === id);
    return obj ? obj.facility : '';
  };

  const selectHistoryViewType = (type: string) => {
    setHistoryViewType(type);
  };

  const minStartDate = useMemo(
    () => date.convertToDayOfYear(sub(new Date(), { years: 2 })),
    []
  );
  const maxStartDate = useMemo(
    () =>
      userInputs.endday
        ? userInputs.endday
        : date.convertToDayOfYear(new Date()),
    [userInputs.endday]
  );
  const minEndDate = useMemo(
    () =>
      userInputs.endday
        ? userInputs.startday
        : date.convertToDayOfYear(new Date()),
    [userInputs.startday]
  );
  const maxEndDate = useMemo(() => date.convertToDayOfYear(new Date()), []);

  useEffect(() => {
    const userFacility = facilities?.find((item: Facility) => {
      return item.facilityId === selectedFacility;
    });
    setFacilityFieldValue(userFacility?.facility ?? '');
    setFacility(userFacility); // initially, auto populate facility
  }, [facilities, selectedFacility]);

  // get encounters when user user input changes
  useEffect(() => {
    const userInputsCopy = { ...userInputs }; // shallow copying  the state object
    userInputsCopy.startday = `${userInputsCopy.startday.replace(
      /-/g,
      ''
    )}000000`; // time added as start of the day
    userInputsCopy.endday = `${userInputsCopy.endday.replace(/-/g, '')}235959`; // time added as end of the day
    userInputsCopy.facilityId = facility?.facilityId; // selected facility id added
    if (facility) getEncountersQuery(userInputsCopy);
  }, [userInputs, facility]);

  useEffect(() => {
    setRoundHistory(patientRoundHistory);
  }, [patientRoundHistory]);

  // TODO: Can this be done in a saga to not repeat since its repeated across to patient rounds report
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    if (patientToOpen) {
      dispatch(clearPatientRoundHistory());
      dispatch(clearTasksByAccountNum());
      const patientMeta = {
        facilityId: patientToOpen.facilityId,
        unitId: patientToOpen.unitId,
        urn: patientToOpen.patientId,
        mrn: patientToOpen.patientMRN,
        accountNum: patientToOpen.accountNum,
        room: patientToOpen.room,
        bed: patientToOpen.bed,
        firstName: patientToOpen.patientFirstName,
        lastName: patientToOpen.patientLastName
      };
      if (patientToOpen.accountNum?.substring(0, 2) === 'UL') {
        navigate('/patients/profile', {
          state: {
            patientMeta: {
              ...patientMeta,
              lastRoundId: patientToOpen.roundId
            },
            isUnlistedPatient: true
          }
        });
      } else {
        navigate('/patients/profile', {
          state: {
            patientMeta,
            isUnlistedPatient: false
          }
        });
      }
    }
  }, [patientToOpen]);

  return (
    <EncountersContainer data-testid="admin-encounter-view">
      <EncountersSidePanelContainer>
        {showHistory ? (
          <div style={{ width: 'maxContent' }}>
            <BackText className="ms-n1" onClick={handleHideHistory}>
              <NeuIcon>chevron_left</NeuIcon> Go Back
            </BackText>
          </div>
        ) : null}
        {!showHistory ? (
          <>
            <EncountersSidePanelSubheader>
              Encounters
            </EncountersSidePanelSubheader>
            <div style={{ paddingTop: '10px', paddingBottom: '10px' }}>
              <EncounterSidePanelInputLabels>
                Facility
              </EncounterSidePanelInputLabels>
              <Dropdown
                classes="ps-0"
                focused={activeSearch === 'facility'}
                name="facility"
                open={isFacilityOpen}
                placeholder={facilityFieldValue || 'Select a Facility'}
                query={query}
                typeAhead
                styles={{ minWidth: 'fit-content', paddingTop: '3px' }}
                setOpen={setIsFacilityOpen}
                setTypeAhead={setActiveSearch}
                setTypeAheadChange={handleTypeAheadChange}
              >
                {activeSearch === 'facility' &&
                facilitySearchResult &&
                facilitySearchResult.length > 0 ? (
                  facilitySearchResult.map((item: Facility) => (
                    <div
                      key={item.id}
                      data-testid={`Encounter-Side-Panel-Facility-Item-${item.id}`}
                      className={`dropdown-hover-item${
                        item?.facility === facilityFieldValue ? ' selected' : ''
                      }`}
                      aria-selected={item?.facility === facilityFieldValue}
                      role="option"
                      tabIndex={0}
                      style={{ paddingLeft: '0.75em' }}
                      onClick={() => handleFacilitySelected(item)}
                      onKeyDown={() => handleFacilitySelected(item)}
                    >
                      {toTitleCaseSpecial(item.facility)}
                    </div>
                  ))
                ) : (
                  <div className="dropdown-search-no-result">
                    No results found!
                  </div>
                )}
              </Dropdown>

              <EncounterSidePanelInput
                id="Encounter-Side-Panel-MRN"
                label="MRN"
                name="mrn"
                required
                onNeuChange={handleInputs}
              />

              <EncounterSidePanelInput
                id="Encounter-Side-Panel-Account"
                label="Account Number"
                name="accountNumber"
                required
                onNeuChange={handleInputs}
              />

              <EncounterSidePanelInput
                id="Encounter-Side-Panel-First-Name"
                label="First Name"
                name="firstName"
                onNeuChange={handleInputs}
              />

              <EncounterSidePanelInput
                id="Encounter-Side-Panel-Last-Name"
                label="Last Name"
                name="lastName"
                onNeuChange={handleInputs}
              />
            </div>
            <EncountersSidePanelSubheader>
              Date Range
            </EncountersSidePanelSubheader>
            {/* TODO: Needs to be a range datepicker */}
            <EncounterSidePanelInput
              id="Encounter-Side-Panel-Start-Day"
              label="Starting"
              name="startday"
              type="date"
              min={minStartDate}
              max={maxStartDate}
              value={userInputs.startday}
              onNeuChange={handleInputs}
            />
            <EncounterSidePanelInput
              id="Encounter-Side-Panel-End-Day"
              label="Ending"
              name="endday"
              type="date"
              min={minEndDate}
              max={maxEndDate}
              value={userInputs.endday}
              onNeuChange={handleInputs}
            />
          </>
        ) : (
          // encounter history side nav
          <>
            <EncountersSidePanelSubheader>
              Encounters
            </EncountersSidePanelSubheader>
            <div style={{ paddingLeft: '10px' }}>
              <EncounterHistorySidePanelLabels>
                Patient Name
              </EncounterHistorySidePanelLabels>
              <EncounterHistorySidePanelData>
                {`${encounterHistory?.firstname} ${encounterHistory?.lastname}`}
              </EncounterHistorySidePanelData>
              <EncounterHistorySidePanelLabels>
                Facility
              </EncounterHistorySidePanelLabels>
              <EncounterHistorySidePanelData>
                {getAccessPlacesById(encounterHistory?.facility)}
              </EncounterHistorySidePanelData>
              <EncounterHistorySidePanelLabels>
                MRN
              </EncounterHistorySidePanelLabels>
              <EncounterHistorySidePanelData>
                {encounterHistory?.mrn}
              </EncounterHistorySidePanelData>
              <EncounterHistorySidePanelLabels>
                Account Number
              </EncounterHistorySidePanelLabels>
              <EncounterHistorySidePanelData>
                {encounterHistory?.accountnumber}
              </EncounterHistorySidePanelData>
              <EncounterHistorySidePanelLabels>
                Admit Date
              </EncounterHistorySidePanelLabels>
              <EncounterHistorySidePanelData>
                {encounterHistory?.admittime
                  ? date.datePrettier(
                      (
                        parseInt(encounterHistory?.admittime, 10) - 1000000
                      ).toString()
                    )
                  : '--/--/--'}
              </EncounterHistorySidePanelData>
              <EncounterHistorySidePanelLabels>
                Discharge Date
              </EncounterHistorySidePanelLabels>
              <EncounterHistorySidePanelData>
                {encounterHistory?.dischargetime
                  ? date.datePrettier(encounterHistory?.dischargetime)
                  : '--/--/--'}
              </EncounterHistorySidePanelData>
            </div>
          </>
        )}
      </EncountersSidePanelContainer>
      <div
        className="d-flex flex-column w-100"
        style={{
          overflow: 'hidden'
        }}
      >
        {!showHistory ? (
          <EncountersTable
            encountersList={encountersData}
            setEncounterHistory={setEncounterHistory}
            setShowHistory={setShowHistory}
          />
        ) : (
          <>
            <EncountersHistoryTabs>
              <NeuTab color="blue" unit="group">
                <NeuTab
                  data-testid="encounters-history-table-view-tab"
                  active={historyViewType === 'table'}
                  unit="tab"
                  onClick={() => selectHistoryViewType('table')}
                >
                  Table View
                </NeuTab>
                <NeuTab
                  data-testid="encounters-history-json-view-tab"
                  active={historyViewType === 'json'}
                  unit="tab"
                  onClick={() => selectHistoryViewType('json')}
                >
                  JSON
                </NeuTab>
              </NeuTab>
            </EncountersHistoryTabs>
            <EncountersHistoryTableContainer>
              {historyViewType === 'table' ? (
                <EncountersHistoryTable
                  encounterData={encounterHistory}
                  roundHistory={roundHistory}
                  gotoRoundPage={gotoRoundPage}
                />
              ) : (
                <JsonViewContainer>
                  <pre
                    style={{
                      fontFamily: 'monospace',
                      fontWeight: '400',
                      fontSize: '16px',
                      lineHeight: '26px',
                      letterSpacing: '0px',
                      textAlign: 'left'
                    }}
                  >
                    <JsonView>
                      {JSON.stringify(encounterHistory, null, 4)}
                    </JsonView>
                  </pre>
                </JsonViewContainer>
              )}
            </EncountersHistoryTableContainer>
          </>
        )}
      </div>
    </EncountersContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    facilities: state.ConfigReducer.facilities,
    patientRoundHistory: state.ReportReducer.patientRoundHistory,
    selectedFacility: state.AdminReducer.selectedFacility
  };
};

export default connect(mapReduxStateToProps)(Encounters);
